
import Vue, { PropType } from 'vue';
import URLS from '~/URLS';
import { HAS_LOGIN, FORCE_VERIFIED_APPLICATION_CLICK } from '~/router';

// Components
import DetailModuleSkeleton from './skeletons/DetailModuleSkeleton.vue';
import Spinner from '~/components/builders/Spinner.vue';
import MinimalProfileModal from '~/components/profile/modals/MinimalProfileModal.vue';

// Services and types
import { Posting } from '~/services/types/job';
import AuthService from '~/services/AuthService';
import AnalyticService from '~/services/AnalyticService';

// Utils
import uuidv4 from '~/utils/uuidv4';
import dateToString, { dateToStringRaw } from '~/utils/dateToString';
import {
    CandidateProfileScorer,
    CandidateProfileStatus,
} from '~/utils/candidate/CandidateProfileScorer';
import PostingService from '~/services/PostingService';
import MessageService from '~/services/MessageService';
import { AxiosError } from 'axios';
import { ServiceErrorCode, ServiceErrorResponse } from '~/services/types/service';

export default Vue.extend({
    name: 'DetailModule',
    components: { DetailModuleSkeleton, Spinner, MinimalProfileModal },
    props: {
        data: {
            type: Object as PropType<Posting>,
            required: true,
        },
        requestId: {
            type: [String, undefined, null] as PropType<string | undefined | null>,
            default: null,
        },
        impressionEventId: {
            type: [String, undefined, null] as PropType<string | undefined | null>,
            default: undefined,
        },
        isVerified: Boolean,
        isPending: Boolean,
        isLoading: Boolean,
        vFairId: {
            type: [String, null] as PropType<string | null>,
            default: null,
        },
        pavilionId: {
            type: [String, null] as PropType<string | null>,
            default: null,
        },
    },
    data: () => ({
        hasLogIn: HAS_LOGIN,
        forceVerifiedApplicationClick: FORCE_VERIFIED_APPLICATION_CLICK,

        applicationLink: null as string | null,
        loadingApplicationLink: false,
        showProfileModal: false,
    }),
    computed: {
        detailData(): Posting {
            return this.data;
        },
        location(): string {
            if (this.detailData.postingRegion === 'TELECOMMUTE')
                return this.$t('shared.postingModule.postItem.remote').toString();

            return ''.concat(
                this.detailData.location ? this.detailData.location : '',
                this.detailData.country ? ', '.concat(this.detailData.country) : ''
            );
        },
        deadline(): string {
            return dateToString(this.detailData.deadline);
        },
        startDate(): string | undefined {
            return dateToStringRaw(this.detailData.startDate);
        },
        languages(): string[] {
            return this.detailData.languages || [];
        },
        permits(): string[] {
            return this.detailData.workPermitRequirements || [];
        },
        educations(): string[] {
            return this.detailData.preferedEducationFields || [];
        },
        jobCategories(): string[] {
            return this.detailData.preferedJobCategories || [];
        },
        educationLevels(): string[] {
            return this.detailData.preferedEducationLevels || [];
        },
        employmentTypes(): string[] {
            return this.detailData.employmentTypes || [];
        },
        tags(): string[] {
            return this.detailData.tags || [];
        },
    },
    methods: {
        async apply() {
            // IF not logged in, open loginDialog
            if (!AuthService.isLoggedIn() && this.hasLogIn) {
                this.$interfaces.logInModule.toggleOpen();
                return;
            }

            // Check minimum profile requirement
            const scorer: CandidateProfileScorer | null = await this.$store.getters[
                'profileModule/awaitCandidateProfileScorer'
            ]();
            if (scorer === null) {
                console.warn('was not able to get CandidateProfileScorer');
                return;
            }

            const score = scorer.getApplyJobProfileScore();
            if (score.status === CandidateProfileStatus.INCOMPLETE) {
                // TODO: Show dialog
                this.openProfileRequirementDialog();
                return;
            }

            this.applyClick();
        },

        applyClick() {
            // Log apply-button clicked
            AnalyticService.applyClickEvent(
                this.detailData.jobAdId,
                this.detailData.companyId,
                this.detailData.title,
                this.vFairId,
                this.pavilionId
            );

            // Send event-request to Google CTS
            if (typeof this.requestId === 'string' && this.requestId.length > 0) {
                this.$interfaces.event.post({
                    body: {
                        requestId: this.requestId,
                        eventId: 'JobApplyNowPressed' + uuidv4(),
                        parentEventId: this.impressionEventId,
                        createTime: new Date().toISOString(),
                        jobEvent: {
                            type: 'APPLICATION_REDIRECT',
                            jobs: [this.data.nameId],
                        },
                    },
                });
            }

            // Open for apply
            if (this.applicationLink) {
                window.open(this.applicationLink, '_blank');
            } else {
                this.loadingApplicationLink = true;

                const service = new PostingService();
                service
                    .getJobApplicationInfo(this.detailData.id)
                    .then(applicationInfo => {
                        this.applicationLink =
                            applicationInfo.uris.length > 0 ? applicationInfo.uris[0] : null;
                    })
                    .catch((error: AxiosError<ServiceErrorResponse>) => {
                        const showDefaultErrorMessage = () => {
                            MessageService.error(
                                this.$i18n.t('jobdetail.errors.applicationLink.title').toString(),
                                this.$i18n.t('jobdetail.errors.applicationLink.message').toString()
                            );
                        };

                        if (!error.response || error.response.data.length === 0) {
                            showDefaultErrorMessage();
                            return;
                        }

                        const errMessage = error.response!.data[0];
                        switch (errMessage.code) {
                            case ServiceErrorCode.PROFILE_INCOMPLETE: {
                                this.openProfileRequirementDialog();
                                break;
                            }
                            default: {
                                showDefaultErrorMessage();
                            }
                        }
                    })
                    .finally(() => {
                        this.loadingApplicationLink = false;
                        if (this.applicationLink) {
                            window.open(this.applicationLink, '_blank');
                        }
                    });
            }
        },
        openProfileRequirementDialog() {
            this.showProfileModal = true;
        },
        handleRegister() {
            this.showProfileModal = false;
            this.apply();
        },
        toAboutGPTW() {
            this.$router.push(URLS.aboutGPTW);
        },
    },
});
